import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { FolderKanban, ArrowUpRight } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_app/projects/")({
  component: ProjectsPage,
});

interface ProjectCard {
  id: string;
  name: string;
  slug: string;
  status: string;
  health: string;
  type: string;
  avatarR2Key: string | null;
  coverR2Key: string | null;
  description: string | null;
  startedAt: number | null;
  targetEndAt: number | null;
  updatedAt: number | null;
  posthogEnabled: boolean;
  hasPlane: boolean;
  categories: Array<{ id: string; name: string; slug: string; color: string | null }>;
}

const STATUS_LABEL: Record<string, string> = {
  discovery: "Discovery",
  active: "Activo",
  paused: "Pausado",
  completed: "Completado",
  archived: "Archivado",
};
const STATUS_BADGE: Record<string, string> = {
  discovery: "badge-info",
  active: "badge-success",
  paused: "badge-warning",
  completed: "badge-ghost",
  archived: "badge-ghost",
};
const HEALTH_DOT: Record<string, string> = {
  green: "bg-success",
  yellow: "bg-warning",
  red: "bg-error",
  unknown: "bg-base-300",
};

function ProjectsPage() {
  const { data, isLoading } = useQuery<{ projects: ProjectCard[] }>({
    queryKey: ["projects"],
    queryFn: () => api.get("/projects"),
  });
  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Proyectos"
        title="Tus proyectos con Axorial"
        subtitle="Cada proyecto tiene su tablero de tickets, links y archivos."
      />
      {isLoading ? (
        <div className="grid place-items-center py-16">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      ) : !data?.projects?.length ? (
        <div className="card-elevated p-10 text-center text-sm text-base-content/55">
          Aún no tienes proyectos visibles. Si crees que es un error,
          contacta a tu lead en Axorial.
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.projects.map((p) => (
            <li key={p.id}>
              <Link
                to="/projects/$projectId"
                params={{ projectId: p.id }}
                className="card-elevated block hover:[&]:card-elevated-hover h-full overflow-hidden"
              >
                {p.coverR2Key ? (
                  <div className="aspect-[3/1] bg-base-200 overflow-hidden">
                    <img
                      src={`/api/projects/${p.id}/cover?v=${p.updatedAt ?? ""}`}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="aspect-[3/1] bg-gradient-to-br from-primary/20 to-secondary/20" />
                )}
                <div className="p-5 -mt-8 relative">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    {p.avatarR2Key ? (
                      <div className="size-12 rounded-lg overflow-hidden ring-4 ring-base-100 bg-base-100">
                        <img
                          src={`/api/projects/${p.id}/avatar?v=${p.updatedAt ?? ""}`}
                          alt={p.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="size-12 rounded-lg bg-primary/15 text-primary grid place-items-center ring-4 ring-base-100">
                        <FolderKanban className="size-5" />
                      </div>
                    )}
                    <ArrowUpRight className="size-4 text-base-content/40 mt-9" />
                  </div>
                  <h3 className="font-display font-semibold text-base leading-tight mb-1.5">
                    {p.name}
                  </h3>
                  <div className="flex items-center gap-2 flex-wrap text-xs mb-2">
                    <span className={`badge badge-xs ${STATUS_BADGE[p.status] ?? "badge-ghost"}`}>
                      {STATUS_LABEL[p.status] ?? p.status}
                    </span>
                    <span className="inline-flex items-center gap-1 text-base-content/55">
                      <span
                        className={`inline-block size-2 rounded-full ${HEALTH_DOT[p.health] ?? "bg-base-300"}`}
                      />
                      {p.health}
                    </span>
                  </div>
                  {p.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {p.categories.slice(0, 4).map((c) => (
                        <span
                          key={c.id}
                          className="badge badge-xs badge-outline"
                          style={c.color ? { borderColor: c.color, color: c.color } : undefined}
                        >
                          {c.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {p.description && (
                    <p className="text-xs text-base-content/65 line-clamp-3 leading-relaxed">
                      {p.description}
                    </p>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
